import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { FamilyModel } from 'src/app/models/archive/family.model';
import { FamilyService } from 'src/app/services/archive/family.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-family',
  templateUrl: './register-family.component.html',
  styleUrls: ['./register-family.component.scss'],
  standalone: false
})
export class RegisterFamilyComponent implements OnInit {

  disabledForm: boolean = false;
  familyId: number = 0;
  familyContract: FamilyModel = new FamilyModel();
  isOpened: string | undefined = undefined;
  nameWindow: string = 'Captura Familias';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = 'family';
  formFamily: FormGroup = new FormGroup({});

  constructor(private activatedRoute: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private familyService: FamilyService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) {
    this.validateForm(this.familyContract);
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.familyId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `No tiene permisos para eliminar una familia. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la familia?')) {
      try {
        await firstValueFrom(this.familyService.deleteFamily(this.familyContract.familyId));
        this.toastHandlerService.showToast(
          'success',
          'La familia fue eliminada exitosamente.'
        );
        this.createNewFamily();
      } catch (error) {
        console.error('Error al eliminar la familia.', error);
      }
    }
  }

  changeFamily(code: string) {
    if (code?.trim() != '') {
      this.getFamilyByCode(code);
    } else {
      this.createNewFamily();
    }
  }

  createNewFamily() {
    if (this.permissionUpdatable) {
      this.familyId = 0;
      this.familyContract = new FamilyModel();
      this.validateForm(this.familyContract);
      this.updateFormPermision();
      this.location.replaceState(`/${this.url}/register`);
    } else {
      this.updateFormPermision();
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para crear familias. Comuníquese con el administrador del sistema.`
      );
    }
  }

  async getFamilyByCode(code: string) {
    try {
      const response = await firstValueFrom(this.familyService.getFamilyByCode(code));
      this.familyId = response.familyId;
      this.familyContract = response;
      this.validateForm(this.familyContract);
      this.location.replaceState(`/${this.url}/register/${this.familyId}`);
    } catch (error) {
      console.error('Error al obtener la familia por código.', error);
    }
  }

  async getFamilyById() {
    try {
      this.familyContract = await firstValueFrom(this.familyService.getFamilyById(this.familyId));
      this.validateForm(this.familyContract)
    } catch (error) {
      console.error('Error al obtener la familia por id.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionProfileByUrl();
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      this.permissionUpdatable = false;
      console.error('Error al cargar permisos.', error);
    }
  }

  async handleInitialState() {
    if (this.familyId !== null && this.familyId !== 0) {
      this.getFamilyById();
    } else {
      this.createNewFamily();
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formFamily.valid) {
      const modelFamily = this.formFamily.value as FamilyModel;
      if (this.familyId == 0) {
        try {
          const response = await firstValueFrom(this.familyService.createFamily(modelFamily));
          this.familyId = response.familyId;
          this.familyContract = response;
          this.validateForm(this.familyContract);
          this.location.replaceState(`/${this.url}/register/${this.familyId}`);
          this.toastHandlerService.showToast(
            'success',
            `La familia fue creada exitosamente.`
          );
        } catch (error) {
          console.error('Error al crear la familia.', error);
        }
      } else {
        this.familyContract.name = modelFamily.name;
        this.familyContract.alterCode = modelFamily.alterCode;
        try {
          const response = await firstValueFrom(this.familyService.updateFamily(this.familyContract));
          this.familyContract = response;
          this.validateForm(this.familyContract);
          this.toastHandlerService.showToast(
            'success',
            `La familia fue modificada exitosamente.`
          );
        } catch (error) {
          console.error('Error al modificar la familia.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'El campo Código es obligatorio.',
        maxlength: 'El Código no puede superar el tamaño máximo de 3 caracteres.',
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede superar el tamaño máximo de 50 caracteres.',
      },
      alterCode: {
        maxlength: 'El Código Alterno no puede superar el tamaño máximo de 10 caracteres.',
      },
    };

    Object.keys(controlMessages).forEach((controlName) => {
      const formControl = this.formFamily.get(controlName);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[controlName];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formFamily.enable());
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formFamily.disable());
    }
  }

  validateForm(family: FamilyModel) {
    this.formFamily = this.formBuilder.group({
      code: [family.code, [Validators.required, Validators.maxLength(3)]],
      name: [family.name, [Validators.required, Validators.maxLength(50)]],
      alterCode: [family.alterCode, [Validators.maxLength(10)]]
    });
  }

  validateFormControls() {
    Object.keys(this.formFamily.controls).forEach((key) => {
      const control = this.formFamily.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
